import type { BillingStatus } from '../context/AuthContext';
import { ROUTES } from '../navigation/routes';
import { canOpenMentorEngagements, hasEffectiveBillingAccess } from './billingAccess';

export type IntakeContinueRoute =
  | typeof ROUTES.MENTOR_ENGAGEMENTS
  | typeof ROUTES.GATED_PLAN_SELECTION
  | typeof ROUTES.ACTIVATION_UPLOAD_FIRST_SESSION;

export interface IntakeContinueInput {
  billingStatus: BillingStatus | null | undefined;
  userType: string | null | undefined;
  canAccessIndividualPaywall: boolean;
  profileType: string;
  planKind: string | null | undefined;
  hasPlan: boolean;
}

export interface IntakeContinueDecision {
  hasBillingAccess: boolean;
  isMentorWorkspace: boolean;
  goesToPlanSelection: boolean;
  goesToMentorEngagements: boolean;
  route: IntakeContinueRoute;
}

/**
 * Where the intake reveal CTA sends the user. Billing access must come from
 * hasEffectiveBillingAccess (which honours school-course access) — a missing
 * billing payload (null) reads as "no access" so unpaid users go to plan
 * selection rather than into the gated app.
 *
 * Mentees is tier3-only: a mentor profile goes there only with the
 * `mentor_engagements` entitlement. Unpaid mentors pay first; mentors on a
 * lower tier take the regular first-session path instead of a locked screen.
 */
export function resolveIntakeContinueRoute(input: IntakeContinueInput): IntakeContinueDecision {
  const { billingStatus, userType, profileType, planKind, hasPlan } = input;

  const hasPlanAccess = hasEffectiveBillingAccess(billingStatus, {
    requireBoundedB2CTrial: input.canAccessIndividualPaywall,
  });
  const hasBillingAccess = hasPlanAccess || Boolean(billingStatus?.has_purchased_credits);
  const goesToPlanSelection = userType === 'b2c' && !hasBillingAccess;

  // Billing status resolves school tiers on the server. Do not trust the
  // persona or user.plan_tier as an entitlement, nor credits as a plan.
  const hasMentorEntitlement = canOpenMentorEngagements(billingStatus, {
    userType,
    requireBoundedB2CTrial: input.canAccessIndividualPaywall,
  });
  const isMentorWorkspace =
    hasPlan && (planKind === 'mentor_workspace' || planKind === 'mentor_plan' || (!planKind && profileType === 'mentor'));
  const isMentorProfile = isMentorWorkspace || profileType === 'mentor';
  const goesToMentorEngagements = !goesToPlanSelection && isMentorProfile && hasMentorEntitlement;

  const route: IntakeContinueRoute = goesToPlanSelection
    ? ROUTES.GATED_PLAN_SELECTION
    : goesToMentorEngagements
      ? ROUTES.MENTOR_ENGAGEMENTS
      : ROUTES.ACTIVATION_UPLOAD_FIRST_SESSION;

  return { hasBillingAccess, isMentorWorkspace, goesToPlanSelection, goesToMentorEngagements, route };
}
